import { useCallback, useEffect, useMemo, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { matchPath, useLocation } from "react-router-dom";

import { useNavigationPersona } from "../../../auth/navigationPersona";
import { usePermission } from "../../../auth/permissions/usePermission";
import type { FeatureMetadata } from "../../../features/feature.types";
import { FEATURES_BY_MODULE, getFeatureById } from "../../../features/featureRegistry";
import { getBestAccessiblePath } from "../../../navigation/bestAccessibleRoute";
import {
  buildMenuSectionsFromFeatures,
  DEFAULT_FEATURE_ICON,
  getModuleSidebarIconTone,
} from "../../../navigation/menuConfig";
import type { RootState } from "../../../redux/store";

import type { SidebarBrandingModel } from "./SidebarBranding";

export { getModuleSidebarIconTone };

function deriveShortName(name: string): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "SMS";
  if (/^[A-Z0-9]{2,8}$/.test(trimmed)) return trimmed;
  const parts = trimmed
    .replace(/[^a-zA-Z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  const initials = parts
    .slice(0, 5)
    .map((p) => p.charAt(0).toUpperCase())
    .join("");
  return initials || "SMS";
}

export type SidebarDashboardItem = {
  id: string;
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  permission: string;
};

export type SidebarStateResult = {
  branding: SidebarBrandingModel;
  homeHref: string;
  dashboardItem: SidebarDashboardItem | null;
  shouldPinDashboard: boolean;
  isDashboardActive: boolean;
  moduleGroups: ReturnType<typeof buildMenuSectionsFromFeatures>[number]["groups"];
  expandedSections: Record<string, boolean>;
  sidebarWidthClass: string;
  toggleSection: (key: string) => void;
  onLogoUrlChange: (nextLogoUrl: string | undefined) => void;
  doesGroupContainPath: (
    items: readonly { href: string; children?: readonly { href: string }[] }[],
  ) => boolean;
};

export function useSidebarState(collapsed: boolean): SidebarStateResult {
  const location = useLocation();
  const { user, tenantBranding } = useSelector(
    (state: RootState) => ({
      user: state.auth?.user ?? null,
      tenantBranding: state.tenantBranding.current,
    }),
    shallowEqual,
  );

  const doesMenuItemMatchPath = useCallback(
    (href: string) => Boolean(matchPath({ path: href, end: false }, location.pathname)),
    [location.pathname],
  );

  const doesGroupContainPath = useCallback(
    (items: readonly { href: string; children?: readonly { href: string }[] }[]) =>
      items.some(
        (item) =>
          doesMenuItemMatchPath(item.href) ||
          Boolean(item.children?.some((c) => doesMenuItemMatchPath(c.href))),
      ),
    [doesMenuItemMatchPath],
  );

  const brandingEntityKey = useMemo(() => {
    const id = (user?.schoolId ?? user?.tenantId ?? "default").toString();
    return id || "default";
  }, [user?.schoolId, user?.tenantId]);

  const [logoOverride, setLogoOverride] = useState<string | undefined>(() => {
    try {
      return (
        localStorage.getItem(`sms.branding.logoUrl.${brandingEntityKey}`) ?? undefined
      );
    } catch {
      return undefined;
    }
  });

  useEffect(() => {
    try {
      setLogoOverride(
        localStorage.getItem(`sms.branding.logoUrl.${brandingEntityKey}`) ?? undefined,
      );
    } catch {
      setLogoOverride(undefined);
    }
  }, [brandingEntityKey]);

  const onLogoUrlChange = useCallback(
    (nextLogoUrl: string | undefined) => {
      setLogoOverride(nextLogoUrl);
      try {
        const key = `sms.branding.logoUrl.${brandingEntityKey}`;
        if (nextLogoUrl) localStorage.setItem(key, nextLogoUrl);
        else localStorage.removeItem(key);
      } catch {
        return;
      }
    },
    [brandingEntityKey],
  );

  const branding = useMemo<SidebarBrandingModel>(() => {
    const fullName = tenantBranding?.name ?? user?.tenantName ?? "SMS";
    const resolvedLogoUrl = tenantBranding?.logoUrl ?? logoOverride;
    return {
      entityKey: brandingEntityKey,
      shortName: deriveShortName(fullName),
      fullName,
      logoUrl: resolvedLogoUrl,
    };
  }, [
    brandingEntityKey,
    logoOverride,
    tenantBranding?.logoUrl,
    tenantBranding?.name,
    user?.tenantName,
  ]);

  const { can } = usePermission();
  const navigationPersona = useNavigationPersona();
  const isGuardianMode = navigationPersona === "guardian";

  const homeHref = useMemo(
    () =>
      getBestAccessiblePath(can, {
        preferredFeatureIds: ["dashboard"],
        fallbackPath: "/403",
        persona: navigationPersona,
      }),
    [can, navigationPersona],
  );

  const dashboardFeature = useMemo(() => getFeatureById("dashboard"), []);

  const featuresByModuleForSidebar = useMemo(() => {
    const next: Record<string, readonly FeatureMetadata[]> = {};
    for (const [moduleKey, features] of Object.entries(FEATURES_BY_MODULE)) {
      const filtered =
        moduleKey === "home" && !isGuardianMode
          ? features.filter((f) => f.id !== "dashboard")
          : features;
      if (filtered.length > 0) next[moduleKey] = filtered;
    }
    return next;
  }, [isGuardianMode]);

  const sections = useMemo(
    () =>
      buildMenuSectionsFromFeatures(featuresByModuleForSidebar, can, {
        persona: navigationPersona,
      }),
    [featuresByModuleForSidebar, can, navigationPersona],
  );

  const moduleGroups = useMemo(() => sections.flatMap((s) => s.groups), [sections]);

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(
    () => ({}),
  );

  useEffect(() => {
    const allowed = new Set(moduleGroups.map((g) => g.id));
    setExpandedSections((prev) => {
      const next: Record<string, boolean> = {};
      for (const [key, value] of Object.entries(prev)) {
        if (allowed.has(key)) next[key] = value;
      }
      return next;
    });
  }, [moduleGroups]);

  useEffect(() => {
    const activeGroup = moduleGroups.find((g) => doesGroupContainPath(g.items));
    if (activeGroup?.id)
      setExpandedSections((prev) => ({ ...prev, [activeGroup.id]: true }));
  }, [doesGroupContainPath, location.pathname, moduleGroups]);

  const toggleSection = useCallback((key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const dashboardItem = useMemo<SidebarDashboardItem | null>(() => {
    if (!dashboardFeature) return null;
    return {
      id: dashboardFeature.id,
      name: dashboardFeature.navigation.label,
      href: dashboardFeature.navigation.path,
      icon: dashboardFeature.navigation.icon ?? DEFAULT_FEATURE_ICON,
      permission: dashboardFeature.permissions.view,
    };
  }, [dashboardFeature]);

  const canSeeDashboard = Boolean(dashboardItem && can(dashboardItem.permission));
  const shouldPinDashboard = canSeeDashboard && !isGuardianMode;
  const isDashboardActive = Boolean(
    dashboardItem &&
    matchPath({ path: dashboardItem.href, end: false }, location.pathname),
  );
  const sidebarWidthClass = collapsed ? "w-16" : "w-56";

  return {
    branding,
    homeHref,
    dashboardItem,
    shouldPinDashboard,
    isDashboardActive,
    moduleGroups,
    expandedSections,
    sidebarWidthClass,
    toggleSection,
    onLogoUrlChange,
    doesGroupContainPath,
  };
}
